<template>
  <div class="py-5">
    <div class="container">
      <div class="row">
        <div class="col-12 col-lg-6">
          <div v-if="profileLoading" class="text-center py-5">
            <b-spinner variant="secondary"></b-spinner>
          </div>
          <template v-else-if="!profileLoading && profile">
            <div class="profile mb-5">
              <div class="profile--header d-flex align-items-center mb-2">
                <div>
                  <div class="position-relative d-inline-block">
                    <div class="profile--avatar mr-2 mr-lg-3">
                      <div v-if="avatarUploading" class="avatar-upload-overlay">
                        <b-spinner variant="secondary" type="grow"></b-spinner>
                      </div>
                      <img :src="profile.avatar"/>
                    </div>
                    <div class="profile--avatar__file">
                      <input type="file"
                            @change="changeAvatar"
                            name="profile-avatar"
                            class="input-file"
                            accept="image/*"
                            :disabled="avatarUploading">
                      <div class="icon-container">
                        <b-icon icon="pencil-square" class="pointer"></b-icon>
                      </div>
                    </div>
                  </div>
                  <div v-if="avatarErrors.length">
                    <div v-for="error in avatarErrors" :key="error" class="text-danger">
                      {{error}}
                    </div>
                  </div>
                </div>
                <div>
                  <h1 class="bold text-lg">{{profile.display_name}}</h1>
                </div>
              </div>
              <div class="profile--data text-sm">
                <b-form @submit="updateUserProfile" @submit.stop.prevent>
                  <div>
                    <p class="text-lowercase">{{$t('auth.firstName')}}:</p>
                    <b-input-group v-if="firstNameEditable" id="input-group-name">
                      <b-form-input id="input-firstName"
                                    class="text-sm"
                                    v-model="form.firstName"
                                    @input="watchForFormChanges"
                                    type="text"
                                    :placeholder="$t('auth.firstName')"
                                    :disabled="formSending"
                      ></b-form-input>
                    </b-input-group>
                    <p v-else>
                      <span v-if="profile.first_name" class="bold">{{profile.first_name}}</span>
                      <span v-else class="font-italic">{{$t('auth.notSet')}}</span>
                      <b-icon @click="firstNameEditable = true" icon="pencil-square" class="pointer ml-2"></b-icon>
                    </p>
                  </div>
                  <div>
                    <p class="text-lowercase">{{$t('auth.lastName')}}:</p>
                    <b-input-group v-if="lastNameEditable" id="input-group-name">
                      <b-form-input id="input-lastName"
                                    class="text-sm"
                                    v-model="form.lastName"
                                    @input="watchForFormChanges"
                                    type="text"
                                    :placeholder="$t('auth.lastName')"
                                    :disabled="formSending"
                      ></b-form-input>
                    </b-input-group>
                    <p v-else>
                      <span v-if="profile.last_name" class="bold">{{profile.last_name}}</span>
                      <span v-else class="font-italic">{{$t('auth.notSet')}}</span>
                      <b-icon @click="lastNameEditable = true" icon="pencil-square" class="pointer ml-2"></b-icon>
                    </p>
                  </div>
                  <div>
                    <p class="text-lowercase">{{$t('forms.emailAddress')}}:</p>
                    <p>
                      <span class="bold">{{profile.user_email}}</span>
                    </p>
                  </div>
                  <div>
                    <p class="text-lowercase">{{$t('auth.accountType')}}:</p>
                    <p>
                      <span v-for="(role, idx) in profile.roles" :key="idx">
                        <span v-if="role !== 'subscriber'" class="role bold">{{formatUserRole(role)}}</span>
                      </span>
                    </p>
                  </div>
                  <button v-if="profileDataChanged"
                          :disabled="formSending"
                          class="text-sm btn btn-primary mt-5 d-flex align-items-center">
                    <span>{{$t('auth.saveChanges')}}</span>
                    <b-spinner v-if="formSending" small class="ml-2"></b-spinner>
                  </button>
                </b-form>
              </div>
            </div>
            <div class="d-flex">
              <div>
                <button v-if="!resetPasswordSentSuccess && !resetPasswordSentFailure"
                        @click="resetPassword"
                        :disabled="resetPasswordSending"
                        class="btn btn-secondary btn-rounded text-sm px-4 d-flex align-items-center">
                  {{$t('auth.resetPassword')}}
                  <b-spinner v-if="resetPasswordSending" small class="ml-3"></b-spinner>
                </button>
                <b-alert v-if="resetPasswordSentFailure" show variant="danger">
                  <span class="text-sm">{{$t('common.connectionError')}}</span>
                </b-alert>
                <b-alert v-if="resetPasswordSentSuccess" show variant="success">
                  <span class="text-sm">{{$t('auth.resetPasswordSent')}}</span>
                </b-alert>
              </div>
              <div class="ml-4">
                <button class="btn btn-danger btn-rounded text-sm px-4 d-flex align-items-center"
                        v-b-modal.modal-delete-account>
                  {{$t('auth.deleteMyAccount')}}
                </button>
                <b-modal id="modal-delete-account"
                         no-close-on-backdrop>
                  <template #modal-header>
                    <h5 class="text-md bold mb-0">{{$t('auth.deleteMyAccount')}}</h5>
                  </template>
                  <p v-if="!deleteAccountSuccess" class="text-sm mb-0" v-html="$t('auth.deleteAccountInfo')"></p>
                  <p v-else class="text-sm mb-0">
                    {{$t('auth.deleteAccountSuccess')}}
                  </p>
                  <template #modal-footer>
                    <div v-if="!deleteAccountSuccess" class="d-flex flex-wrap align-items-center justify-content-end">
                      <div v-if="deleteAccountError">
                        <p class="text-xs text-danger mb-2">{{deleteAccountError}}</p>
                      </div>
                      <button class="btn btn-secondary btn-rounded text-sm px-4 d-flex align-items-center"
                              @click="$bvModal.hide('modal-delete-account')"
                              :disabled="deletingAccount">
                        {{$t('common.cancel')}}
                      </button>
                      <button class="btn btn-danger btn-rounded text-sm px-4 ml-2 d-flex align-items-center"
                              :disabled="deletingAccount"
                              @click="deleteAccount()">
                        {{$t('auth.proceedToRemove')}}
                        <b-spinner v-if="deletingAccount" small class="ml-2"></b-spinner>
                      </button>
                    </div>
                    <div v-else>
                      <button class="btn btn-secondary btn-rounded text-sm px-4 d-flex align-items-center"
                              :disabled="deletingAccount"
                              @click="continueAfterDeleteAccount()">
                        {{$t('common.continue')}}
                      </button>
                    </div>
                  </template>
                </b-modal>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import mime from 'mime-types';
import UploadService from '../services/upload.service';
import { IMAGES_UPLOAD_MIME_TYPES } from '../config';

const uploadService = new UploadService();

export default {
  metaInfo() {
    return {
      title: `${this.$t('auth.profile')} - Nissens Support`,
    };
  },
  data() {
    return {
      resetPasswordSending: false,
      resetPasswordSentSuccess: false,
      resetPasswordSentFailure: false,
      firstNameEditable: false,
      lastNameEditable: false,
      profileDataChanged: false,
      form: {
        firstName: '',
        lastName: '',
      },
      formSending: false,
      deletingAccount: false,
      deleteAccountSuccess: false,
      deleteAccountError: '',
      avatarErrors: [],
      avatarUploading: false,
    };
  },
  computed: {
    loggedIn() {
      return this.$store.state.user.status.loggedIn;
    },
    ...mapGetters({
      profile: 'user/profile',
      profileLoading: 'user/profileLoading',
      user: 'user/user',
    }),
  },
  mounted() {
    if (!this.loggedIn) {
      this.$router.push('/');
    }
    this.$store.dispatch('user/GET_USER_PROFILE').then((profile) => {
      if (profile) {
        this.form.firstName = profile.first_name;
        this.form.lastName = profile.last_name;
      }
    });
  },
  methods: {
    resetPassword() {
      this.resetPasswordSending = true;
      this.resetPasswordSentSuccess = false;
      this.resetPasswordSentFailure = false;
      this.$store.dispatch('user/SEND_RESET_PASSWORD', { email: this.user.user_email }).then(() => {
        this.resetPasswordSending = false;
        this.resetPasswordSentSuccess = true;
      }).catch(() => {
        this.resetPasswordSending = false;
        this.resetPasswordSentFailure = true;
      });
    },
    updateUserProfile() {
      this.formSending = true;
      const userData = {
        first_name: this.form.firstName,
        last_name: this.form.lastName,
      };
      this.$store.dispatch('user/UPDATE_USER_PROFILE', { ...userData }).then(() => {
        this.formSending = false;
        this.firstNameEditable = false;
        this.lastNameEditable = false;
        this.watchForFormChanges();
        this.showSuccessMessage();
      }).catch(() => {
        this.formSending = false;
        this.showErrorMessage();
      });
    },
    watchForFormChanges() {
      if (
        this.form.firstName !== this.profile.first_name
        || this.form.lastName !== this.profile.last_name
      ) {
        this.profileDataChanged = true;
      } else {
        this.profileDataChanged = false;
      }
    },
    deleteAccount() {
      this.deletingAccount = true;
      this.deleteAccountError = '';
      this.$store.dispatch('user/DELETE_USER_ACCOUNT')
        .then(() => {
          this.deletingAccount = false;
          this.deleteAccountSuccess = true;
        })
        .catch((message) => {
          this.deletingAccount = false;
          this.deleteAccountError = message;
        });
    },
    async changeAvatar(event) {
      const file = event.target.files[0];
      this.avatarErrors = [];
      if (file) {
        if (!uploadService.isMimeTypeValid(file, IMAGES_UPLOAD_MIME_TYPES)) this.avatarErrors.push(this.$t('forms.fileTypeNotValid'));
        if (!uploadService.isFileSizeValid(file)) this.avatarErrors.push(this.$t('forms.fileSizeNotValid'));
        if (!this.avatarErrors.length) {
          this.avatarUploading = true;
          const fileToUpload = {
            file,
            fileName: `avatar_${this.user.ID}.${mime.extension(file.type)}`,
          };
          const uploadedAvatar = await uploadService.uploadMediaAndGetResponse([fileToUpload]);
          if (uploadedAvatar[0]?.id) {
            const dataToSend = {
              acf: {
                avatar: uploadedAvatar[0].id,
              },
            };
            this.$store.dispatch('user/UPDATE_USER_PROFILE', { ...dataToSend }).then(() => {
              this.avatarUploading = false;
              this.showSuccessMessage();
            }).catch(() => {
              this.avatarUploading = false;
              this.showErrorMessage();
            });
          } else {
            this.avatarUploading = false;
            this.showErrorMessage();
          }
        }
      }
    },
    showSuccessMessage() {
      this.$bvToast.toast('Success', {
        title: this.$t('auth.profileUpdatedSuccess'),
        variant: 'light',
        solid: true,
        autoHideDelay: 5000,
        toaster: 'b-toaster-bottom-center',
        bodyClass: 'd-none',
        toastClass: 'toast-secondary',
      });
    },
    showErrorMessage() {
      this.$bvToast.toast('Error', {
        title: this.$t('common.connectionError'),
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000,
        toaster: 'b-toaster-bottom-center',
        bodyClass: 'd-none',
      });
    },
    continueAfterDeleteAccount() {
      this.$router.push('/');
      this.$store.dispatch('user/LOGOUT');
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/common.scss';
.profile {
  &--header {
    padding-left: 30px;
    h1 {
      margin: 0;
      color: $secondary;
    }
  }
  &--data {
    padding: 15px 30px;
    border-left: 1px solid;
    input[type=text] {
      max-width: 400px;
    }
    form > div {
      &:not(:last-child) {
        margin-bottom: 1rem;
      }
      p:first-child, svg {
        color: #7c7c7c;
      }
    }
    p {
      margin: 0;
    }
  }
  &--avatar {
    border-radius: 50%;
    overflow: hidden;
    width: 100px;
    height: 100px;
    position: relative;
    .avatar-upload-overlay {
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background: rgba(255, 255, 255, .8);
    }
    img {
      width: 100%;
      height: 100%;
    }
    &__file {
      position: absolute;
      top: 0; right: 0;
      line-height: 0;
      .input-file {
        opacity: 0;
        line-height: 0;
        width: 15px;
        height: 15px;
        position: absolute;
        top: 0; left: 0;
        cursor: pointer;
        background: red;
      }
      .icon-container {
        display: inline-block;
        height: 15px;
        svg {
          color: #7c7c7c;
        }
      }
    }
    @include media-breakpoint-down(md) {
      width: 60px;
      height: 60px;
    }
  }
}
</style>
